import { Building2 } from "lucide-react";
import { getDevClubOverride, setDevClubOverride } from "@/lib/api";

const BOOLEAN_ENABLE_VALUES = new Set(["1", "true", "yes", "on"]);
const BOOLEAN_DISABLE_VALUES = new Set(["0", "false", "no", "off"]);

/**
 * The local stack historically used `VITE_DEV_CLUB_OVERRIDE=1` as an enable
 * flag, while the switcher later grew support for a comma-separated slug list.
 * Preserve both forms: a boolean enable value exposes the seeded default club;
 * an explicit list remains authoritative for multi-club development.
 */
export function devClubSlugs(raw: string | undefined): string[] {
  if (!raw) return [];
  const normalized = raw.trim().toLowerCase();
  if (BOOLEAN_ENABLE_VALUES.has(normalized)) return ["default"];
  if (!normalized || BOOLEAN_DISABLE_VALUES.has(normalized)) return [];
  return [...new Set(raw.split(",").map((slug) => slug.trim()).filter(Boolean))];
}

/**
 * DEV-only multi-club switcher.
 *
 * Single-localhost Vite has no subdomains, so a developer can't exercise the
 * production Host→club resolution path (`<slug>.tktic.com` → `request.state.club`).
 * When `VITE_DEV_CLUB_OVERRIDE` is set to a comma-separated list of seeded club
 * slugs (e.g. `"default,northgate"`), this floating control lets the developer
 * pick one; the choice is persisted in localStorage and attached as the
 * `X-Tktic-Club-Override` header on every API request (see `devClubOverrideHeader`
 * in `lib/api.ts`). The backend honors that header only when its own
 * `TKTIC_CLUB_DEV_OVERRIDE` gate is on — the two mirror each other exactly.
 *
 * This whole module is gated on `import.meta.env.DEV`, so it (and the override
 * machinery it drives) is tree-shaken out of production bundles. The
 * fidelity/Playwright path uses real `/etc/hosts` subdomains instead; this is
 * the convenience path for everyday single-localhost development.
 *
 * Switching does a full-document navigation (not a soft client route) so the
 * TanStack Query cache and any in-memory club state are reset from scratch —
 * exactly what crossing a real subdomain boundary would do.
 */
export function DevClubSwitcher() {
  if (!import.meta.env.DEV) return null;
  const raw = import.meta.env.VITE_DEV_CLUB_OVERRIDE;
  if (!raw) return null;

  const slugs = devClubSlugs(raw);
  if (slugs.length === 0) return null;

  // This control represents the LOCAL override, not the account's current
  // club. Falling back to `/api/me.current_club` made a bare localhost session
  // look as if "default" was selected even though no override header was being
  // sent; choosing the already-displayed option then emitted no change event.
  const active = getDevClubOverride() ?? "";

  const onPick = (slug: string) => {
    setDevClubOverride(slug || null);
    // Full reload: the new override header takes effect on the next /api/me,
    // and the cache starts empty — mirrors a real cross-subdomain navigation.
    window.location.assign("/");
  };

  return (
    <div
      className="fixed bottom-3 left-3 z-50 flex items-center gap-2 rounded-control border border-rule bg-surface px-2.5 py-1.5 text-xs shadow-sm"
      data-testid="dev-club-switcher"
    >
      <Building2 className="h-3.5 w-3.5 text-ink-muted" aria-hidden="true" />
      <span className="font-display uppercase tracking-wider text-[0.6rem] text-ink-muted">
        Dev club
      </span>
      <select
        aria-label="Dev club override"
        value={active}
        onChange={(e) => onPick(e.target.value)}
        className="bg-field rounded-control px-1.5 py-0.5 text-xs text-ink"
      >
        <option value="">Apex (no club)</option>
        {slugs.map((slug) => (
          <option key={slug} value={slug}>
            {slug}
          </option>
        ))}
      </select>
    </div>
  );
}
